import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Transaction } from '@/types';
import { useCompany } from '@/hooks/useCompany';
import { BillTemplate } from './BillTemplate';
import { EmailBillDialog } from './EmailBillDialog';
import { generateBillPDF } from '@/lib/reports/bill-pdf';
import { generateBillNumber } from '@/services/billingService';
import { Download, Mail, Printer, X } from 'lucide-react';

interface BillModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction: Transaction;
  client?: {
    name: string;
    email?: string;
    phone?: string;
    documentNumber?: string;
    address?: {
      street?: string;
      number?: string;
      complement?: string;
      neighborhood?: string;
      city?: string;
      state?: string;
      zipCode?: string;
    };
  };
  bankDetails?: {
    bank?: string;
    agency?: string;
    account?: string;
    pixKey?: string;
  };
}

export function BillModal({
  open,
  onOpenChange,
  transaction,
  client,
  bankDetails
}: BillModalProps) {
  const { data: company } = useCompany();
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  // Generate bill number
  const billNumber = generateBillNumber(transaction.id);
  const issueDate = new Date();

  // Handle print
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      const printContent = document.getElementById('bill-template-content');
      if (printContent) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Cobrança ${billNumber}</title>
              <style>
                body { font-family: Arial, sans-serif; margin: 20px; }
                @media print { body { margin: 0; } }
              </style>
            </head>
            <body>
              ${printContent.innerHTML}
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 250);
      }
    }
  };

  // Handle PDF download
  const handleDownloadPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      await generateBillPDF({
        transaction,
        client,
        bankDetails,
        billNumber,
        issueDate,
        companyName: company?.name || 'Sua Empresa',
        companyLogo: company?.logo_url || undefined,
        companyDocument: company?.document || undefined,
        companyAddress: company?.address || undefined
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Erro ao gerar PDF. Tente novamente.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Handle email
  const handleSendEmail = () => {
    setIsEmailDialogOpen(true);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Cobrança - {billNumber}</span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onOpenChange(false)}
                className="h-6 w-6"
              >
                <X className="h-4 w-4" />
              </Button>
            </DialogTitle>
            <DialogDescription>
              Visualize e envie a cobrança referente à transação selecionada
            </DialogDescription>
          </DialogHeader>

          {/* Bill Template */}
          <div className="mt-4">
            <div id="bill-template-content">
              <BillTemplate
                transaction={transaction}
                client={client}
                bankDetails={bankDetails}
                billNumber={billNumber}
                issueDate={issueDate}
                companyName={company?.name || 'Sua Empresa'}
                companyLogo={company?.logo_url}
                companyDocument={company?.document}
                companyAddress={company?.address}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 mt-6 pt-4 border-t">
            <Button
              onClick={handlePrint}
              variant="outline"
              className="flex-1 flex items-center gap-2"
            >
              <Printer className="h-4 w-4" />
              Imprimir
            </Button>

            <Button
              onClick={handleDownloadPDF}
              variant="outline"
              disabled={isGeneratingPDF}
              className="flex-1 flex items-center gap-2"
            >
              {isGeneratingPDF ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              {isGeneratingPDF ? 'Gerando...' : 'Baixar PDF'}
            </Button>

            <Button
              onClick={handleSendEmail}
              className="flex-1 flex items-center gap-2 bg-terracotta-600 hover:bg-terracotta-700"
            >
              <Mail className="h-4 w-4" />
              Enviar por E-mail
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <EmailBillDialog
        open={isEmailDialogOpen}
        onOpenChange={setIsEmailDialogOpen}
        transaction={transaction}
        client={client}
        billNumber={billNumber}
        issueDate={issueDate}
        companyName={company?.name || 'Sua Empresa'}
        companyLogo={company?.logo_url}
        bankDetails={bankDetails}
      />
    </>
  );
}